import React, { useEffect, useState } from 'react';
import useForm from '@afosto/utils-react/validation/useForm';
import useIntl from '@afosto/utils-react/intl/useIntl';
import useLocalStorage from '@afosto/utils-react/useLocalStorage';
import usePermissions from '@afosto/utils-react/permissions/usePermissions';
import { useSnackbar } from 'notistack';
import { FormattedMessage } from 'react-intl';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Autocomplete, { highlightSubstring } from '@afosto/app-components-react/Autocomplete';
import Form from '@afosto/app-components-react/Form';
import FormDrawer from '@afosto/app-components-react/FormDrawer';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import findObjectInArray from '@afosto/utils/array/findObjectInArray';
import { hubs as hubsApi } from '@afosto/spooler/api/Hubs';
import { printers as printersApi } from '@afosto/spooler/api/Printers';
import {
  PARCEL_LABEL_PRINTER_SETTING_KEY,
  PARCEL_LABEL_HUB_SETTING_KEY,
  BARCODE_PRINTER_SETTING_KEY,
  BARCODE_HUB_SETTING_KEY,
  HUB_SETTING_KEY,
  PRINTER_SETTING_KEY,
} from '../../../../utils/constants';
import translations from './translations';
import CardContent from '@material-ui/core/CardContent';

const SpoolerSettings = () => {
  const [hasPermissions, UnauthorizedComponent] = usePermissions([
    'spl:printers:read',
    'spl:hubs:read',
  ]);
  const intl = useIntl();
  const [currentlyEditing, setCurrentlyEditing] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [a4PrinterSettings, setA4PrinterSettings] = useState(null);
  const [labelPrinterSettings, setLabelPrinterSettings] = useState(null);
  const [barcodePrinterSettings, setBarcodePrinterSettings] = useState(null);
  const [printerOptions, setPrinterOptions] = useState([]);
  const { enqueueSnackbar } = useSnackbar();

  const [getLocalStorage, setLocalStorage, removeLocalStorage] = useLocalStorage();

  const handleSetPrinterSettings = (config, setState) => fields => {
    if (fields?.printer) {
      setLocalStorage(config.hubSettingKey, fields.printer.hub_id);
      setLocalStorage(config.printerSettingKey, fields.printer.id);
      setState({
        hubID: getLocalStorage(config.hubSettingKey),
        printerID: getLocalStorage(config.printerSettingKey),
      });
    } else {
      removeLocalStorage(config.hubSettingKey);
      removeLocalStorage(config.printerSettingKey);
      setState(null);
    }

    enqueueSnackbar(intl.formatMessage(translations.feedback.printerSettingsSaved), {
      variant: 'success',
    });
    setCurrentlyEditing(null);
  };

  const fetchHubs = async hubIds => {
    try {
      const promises = [...new Set(hubIds)].map(id => hubsApi.getHub(id));
      return await Promise.allSettled(promises).then(result =>
        result
          .filter(promise => promise.status === 'fulfilled')
          .reduce((acc, response) => [...acc, response.value.data], []),
      );
    } catch (error) {
      return [];
    }
  };

  const a4Form = useForm({
    editInline: false,
    defaultValues: {
      printer: findObjectInArray(printerOptions, 'id', a4PrinterSettings?.printerID) ?? null,
    },
    onSubmit: handleSetPrinterSettings(
      {
        printerSettingKey: PRINTER_SETTING_KEY,
        hubSettingKey: HUB_SETTING_KEY,
      },
      setA4PrinterSettings,
    ),
    onCancel: () => {
      setCurrentlyEditing(null);
    },
  });

  const labelForm = useForm({
    editInline: false,
    defaultValues: {
      printer: findObjectInArray(printerOptions, 'id', labelPrinterSettings?.printerID) ?? null,
    },
    onSubmit: handleSetPrinterSettings(
      {
        printerSettingKey: PARCEL_LABEL_PRINTER_SETTING_KEY,
        hubSettingKey: PARCEL_LABEL_HUB_SETTING_KEY,
      },
      setLabelPrinterSettings,
    ),
    onCancel: () => {
      setCurrentlyEditing(null);
    },
  });

  const barcodeForm = useForm({
    editInline: false,
    defaultValues: {
      printer: findObjectInArray(printerOptions, 'id', barcodePrinterSettings?.printerID) ?? null,
    },
    onSubmit: handleSetPrinterSettings(
      {
        printerSettingKey: BARCODE_PRINTER_SETTING_KEY,
        hubSettingKey: BARCODE_HUB_SETTING_KEY,
      },
      setBarcodePrinterSettings,
    ),
    onCancel: () => {
      setCurrentlyEditing(null);
    },
  });

  const renderSuggestion = (option, { inputValue = '' } = {}) => (
    <ListItem component="div" dense disableGutters>
      <ListItemText
        primary={highlightSubstring(option.name, inputValue)}
        secondary={highlightSubstring(option.hub_name, inputValue)}
      />
    </ListItem>
  );

  useEffect(() => {
    const fetchPrinters = async () => {
      try {
        const { data } = await printersApi.listPrinters();

        const hubIds = data.map(printer => printer.hub_id);

        const hubs = await fetchHubs(hubIds);
        const formattedPrinters = data.reduce((acc, printer) => {
          const formattedPrinter = {
            ...printer,
            hub_name: findObjectInArray(hubs, 'id', printer?.hub_id).name,
          };
          return [...acc, formattedPrinter];
        }, []);

        setPrinterOptions(formattedPrinters);
        return formattedPrinters;
      } catch (error) {
        return [];
      } finally {
        setIsLoading(false);
      }
    };

    setA4PrinterSettings({
      hubID: getLocalStorage(HUB_SETTING_KEY),
      printerID: getLocalStorage(PRINTER_SETTING_KEY),
    });
    setLabelPrinterSettings({
      hubID: getLocalStorage(PARCEL_LABEL_HUB_SETTING_KEY),
      printerID: getLocalStorage(PARCEL_LABEL_PRINTER_SETTING_KEY),
    });
    setBarcodePrinterSettings({
      hubID: getLocalStorage(BARCODE_HUB_SETTING_KEY),
      printerID: getLocalStorage(BARCODE_PRINTER_SETTING_KEY),
    });
    fetchPrinters();
  }, [getLocalStorage]);

  if (!hasPermissions()) {
    return (
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
          subheader={intl.formatMessage(translations.subtitle)}
          subheaderTypographyProps={{ variant: 'caption' }}
        />
        <CardContent>
          <UnauthorizedComponent showActions={false} />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader
          title={intl.formatMessage(translations.title)}
          titleTypographyProps={{ variant: 'h6' }}
          subheader={intl.formatMessage(translations.subtitle)}
          subheaderTypographyProps={{ variant: 'caption' }}
        />
        <LoadingWrapper isLoading={isLoading}>
          <Divider />
          <List dense>
            <ListItem>
              <ListItemText
                primary={intl.formatMessage(translations.a4Printer.title)}
                secondary={
                  findObjectInArray(printerOptions, 'id', a4PrinterSettings?.printerID)?.name ||
                  intl.formatMessage(translations.feedback.noPrinterSelected)
                }
              />
              <ListItemSecondaryAction>
                <Button onClick={() => setCurrentlyEditing('a4')} variant="outlined">
                  <FormattedMessage {...translations.actions.edit} />
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primary={intl.formatMessage(translations.labelPrinter.title)}
                secondary={
                  findObjectInArray(printerOptions, 'id', labelPrinterSettings?.printerID)?.name ||
                  intl.formatMessage(translations.feedback.noPrinterSelected)
                }
              />
              <ListItemSecondaryAction>
                <Button onClick={() => setCurrentlyEditing('label')} variant="outlined">
                  <FormattedMessage {...translations.actions.edit} />
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
            <Divider />
            <ListItem>
              <ListItemText
                primary={intl.formatMessage(translations.barcodePrinter.title)}
                secondary={
                  findObjectInArray(printerOptions, 'id', barcodePrinterSettings?.printerID)
                    ?.name || intl.formatMessage(translations.feedback.noPrinterSelected)
                }
              />
              <ListItemSecondaryAction>
                <Button onClick={() => setCurrentlyEditing('barcode')} variant="outlined">
                  <FormattedMessage {...translations.actions.edit} />
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
          </List>
        </LoadingWrapper>
      </Card>
      <FormDrawer
        isOpen={currentlyEditing === 'a4'}
        title={intl.formatMessage(translations.a4EditDrawer.title)}
        drawerWidth="420px"
        onClose={() => setCurrentlyEditing(null)}
      >
        <LoadingWrapper isLoading={a4Form.isSubmitting}>
          <Form form={a4Form}>
            <Autocomplete
              name="printer"
              label={intl.formatMessage(translations.a4EditDrawer.fields.printer.label)}
              options={printerOptions}
              getOptionLabel={item => item.name}
              renderOption={renderSuggestion}
            />
          </Form>
        </LoadingWrapper>
      </FormDrawer>
      <FormDrawer
        isOpen={currentlyEditing === 'label'}
        title={intl.formatMessage(translations.labelEditDrawer.title)}
        drawerWidth="420px"
        onClose={() => setCurrentlyEditing(null)}
      >
        <LoadingWrapper isLoading={labelForm.isSubmitting}>
          <Form form={labelForm}>
            <Autocomplete
              name="printer"
              label={intl.formatMessage(translations.labelEditDrawer.fields.printer.label)}
              options={printerOptions}
              getOptionLabel={item => item.name}
              renderOption={renderSuggestion}
            />
          </Form>
        </LoadingWrapper>
      </FormDrawer>
      <FormDrawer
        isOpen={currentlyEditing === 'barcode'}
        title={intl.formatMessage(translations.barcodeEditDrawer.title)}
        drawerWidth="420px"
        onClose={() => setCurrentlyEditing(null)}
      >
        <LoadingWrapper isLoading={barcodeForm.isSubmitting}>
          <Form form={barcodeForm}>
            <Autocomplete
              name="printer"
              label={intl.formatMessage(translations.barcodeEditDrawer.fields.printer.label)}
              options={printerOptions}
              getOptionLabel={item => item.name}
              renderOption={renderSuggestion}
            />
          </Form>
        </LoadingWrapper>
      </FormDrawer>
    </>
  );
};

export default SpoolerSettings;
